import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Upload, RotateCcw, Eye, Loader2, Type } from "lucide-react";
import { api } from "@/lib/api";
import { setSiteTextOverrides, SITE_TEXT_PREVIEW_KEY } from "@/lib/siteText";

const PAGE = 60;

// Admin: every visible string on the site — edit, save as draft, publish live.
export default function SiteTextEditor() {
  const [catalog, setCatalog] = useState(null);
  const [draft, setDraft] = useState({});
  const [published, setPublished] = useState({});
  const [pubAt, setPubAt] = useState(null);
  const [raw, setRaw] = useState({ draft: [], published: [] }); // includes "only this spot" edits
  const [edits, setEdits] = useState({});
  const [q, setQ] = useState("");
  const [area, setArea] = useState("all");
  const [only, setOnly] = useState("all"); // all | edited | unpublished
  const [limit, setLimit] = useState(PAGE);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(() => localStorage.getItem(SITE_TEXT_PREVIEW_KEY) === "1");

  const load = async () => {
    const d = await api.adminSiteText();
    setRaw({ draft: d.draft || [], published: d.published || [] });
    setDraft(Object.fromEntries((d.draft || []).filter((x) => !x.s).map((x) => [x.o, x.n])));
    setPublished(Object.fromEntries((d.published || []).filter((x) => !x.s).map((x) => [x.o, x.n])));
    setPubAt(d.published_at);
  };
  useEffect(() => {
    import("@/lib/siteTextCatalog.json").then((m) => setCatalog(m.default || m));
    load().catch(() => toast.error("Could not load site text"));
  }, []);

  const areas = useMemo(() => [...new Set((catalog || []).flatMap((x) => x.a))].sort((a, b) => a.localeCompare(b)), [catalog]);
  const rows = useMemo(() => {
    if (!catalog) return [];
    const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
    const extra = Object.keys(draft).filter((o) => !catalog.some((c) => c.t === o)).map((o) => ({ t: o, k: "text", a: ["(custom)"] }));
    return [...catalog, ...extra].filter((x) => {
      if (area !== "all" && !x.a.includes(area)) return false;
      if (only === "edited" && !draft[x.t]) return false;
      if (only === "unpublished" && (draft[x.t] || "") === (published[x.t] || "")) return false;
      const hay = `${x.t} ${draft[x.t] || ""} ${x.a.join(" ")}`.toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }, [catalog, q, area, only, draft, published]);

  const pendingCount = Object.keys(edits).length;
  const unpublished = useMemo(() => {
    const keys = new Set([...Object.keys(draft), ...Object.keys(published)]);
    const sk = (x) => `${x.s}\u0000${x.o}\u0000${x.n}`;
    const sd = raw.draft.filter((x) => x.s).map(sk), sp = raw.published.filter((x) => x.s).map(sk);
    const spotDiff = sd.filter((k) => !sp.includes(k)).length + sp.filter((k) => !sd.includes(k)).length;
    return [...keys].filter((k) => (draft[k] || "") !== (published[k] || "")).length + spotDiff;
  }, [draft, published, raw]);
  const spotEdits = raw.draft.filter((x) => x.s);
  const revertSpot = async (x) => {
    try { const d = await api.saveSiteTextDraft([{ o: x.o, s: x.s, n: "" }]); if (preview) setSiteTextOverrides(d.draft); await load(); }
    catch { toast.error("Could not revert"); }
  };

  const saveDraft = async () => {
    if (!pendingCount) return;
    setBusy(true);
    try {
      const d = await api.saveSiteTextDraft(Object.entries(edits).map(([o, n]) => ({ o, n })));
      setRaw((r) => ({ ...r, draft: d.draft }));
      setDraft(Object.fromEntries(d.draft.filter((x) => !x.s).map((x) => [x.o, x.n]))); setEdits({});
      if (preview) setSiteTextOverrides(d.draft);
      toast.success("Draft saved — press Publish to make it live");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  const publish = async () => {
    const hadPending = pendingCount > 0;
    if (hadPending) await saveDraft();
    if (!window.confirm(hadPending ? "Publish all draft changes to the live site?" : `Publish ${unpublished} change${unpublished === 1 ? "" : "s"} to the live site?`)) return;
    setBusy(true);
    try { const r = await api.publishSiteText(); setSiteTextOverrides(r.published); await load(); toast.success(`Published — ${r.count} custom text${r.count === 1 ? "" : "s"} live`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); }
    setBusy(false);
  };
  const togglePreview = async () => {
    const on = !preview; setPreview(on);
    localStorage.setItem(SITE_TEXT_PREVIEW_KEY, on ? "1" : "0");
    setSiteTextOverrides(on ? raw.draft : raw.published);
  };

  return (
    <div className="space-y-4" data-testid="site-text-editor" data-site-text-skip="">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-display text-xl font-black text-white"><Type size={20} className="text-[#00F0FF]" /> Edit Site Text</h2>
          <p className="mt-1 text-sm text-[#A1A1AA]">Change any button, label, heading, placeholder or message. Save drafts, preview them on your screen, then publish live. {pubAt && <span className="text-[#666]">Last published {new Date(pubAt).toLocaleString()}.</span>}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={togglePreview} data-testid="site-text-preview-toggle" className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-bold ${preview ? "border-[#FFC94D] bg-[#FFC94D]/15 text-[#FFC94D]" : "border-white/15 text-[#A1A1AA] hover:text-white"}`}><Eye size={14} /> {preview ? "Previewing drafts" : "Preview drafts on my screen"}</button>
          <button onClick={saveDraft} disabled={!pendingCount || busy} data-testid="site-text-save-draft" className="rounded-lg border border-[#00F0FF]/50 px-3 py-2 text-xs font-bold text-[#00F0FF] hover:bg-[#00F0FF]/10 disabled:opacity-40">Save draft {pendingCount ? `(${pendingCount})` : ""}</button>
          <button onClick={publish} disabled={busy || (!unpublished && !pendingCount)} data-testid="site-text-publish" className="flex items-center gap-1.5 rounded-lg bg-[#00F0FF] px-4 py-2 text-xs font-bold text-black hover:bg-[#00F0FF]/90 disabled:opacity-40">{busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Publish live {unpublished ? `(${unpublished})` : ""}</button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-[240px] flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
          <Search size={14} className="text-[#A1A1AA]" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }} placeholder="Search any text on the site, e.g. Export" data-testid="site-text-search" className="w-full bg-transparent text-sm text-white outline-none placeholder:text-[#555]" />
        </div>
        <select value={area} onChange={(e) => { setArea(e.target.value); setLimit(PAGE); }} data-testid="site-text-area" className="rounded-lg border border-white/10 bg-[#141414] px-2 py-2 text-xs text-white">
          <option value="all">All screens</option>{areas.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        {[["all", "All"], ["edited", "Edited"], ["unpublished", "Not published"]].map(([k, l]) => (
          <button key={k} onClick={() => setOnly(k)} data-testid={`site-text-filter-${k}`} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${only === k ? "bg-white/15 text-white" : "text-[#A1A1AA] hover:text-white"}`}>{l}</button>
        ))}
        <span className="text-xs text-[#666]" data-testid="site-text-count">{catalog ? `${rows.length} of ${catalog.length + Object.keys(draft).filter((o) => !catalog.some((c) => c.t === o)).length} texts` : "Loading…"}</span>
      </div>
      {spotEdits.length > 0 && (
        <div className="rounded-xl border border-[#FFC94D]/20 bg-[#FFC94D]/[0.04] p-3" data-testid="site-text-spot-edits">
          <div className="mb-2 text-xs font-bold text-[#FFC94D]">Changed in one spot only ({spotEdits.length}) — made with the floating "Edit text" button</div>
          {spotEdits.map((x) => (
            <div key={`${x.s}|${x.o}`} className="flex items-center gap-2 border-t border-white/5 py-1.5 text-sm" data-testid="site-text-spot-row">
              <span className="min-w-0 flex-1 truncate text-white" title={x.s}>“{x.o}” → “{x.n}” <span className="text-[10px] text-[#666]">{x.s.split(/[#>]/)[0]}</span></span>
              <button onClick={() => revertSpot(x)} title="Back to original" data-testid="site-text-spot-revert" className="rounded p-1.5 text-[#A1A1AA] hover:text-white"><RotateCcw size={14} /></button>
            </div>
          ))}
        </div>
      )}
      {!catalog ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#00F0FF]" /></div> : (
        <div className="overflow-hidden rounded-xl border border-white/10">
          {rows.slice(0, limit).map((x) => {
            const cur = edits[x.t] ?? draft[x.t] ?? "";
            const live = published[x.t];
            return (
              <div key={x.t} className="grid gap-2 border-b border-white/5 px-3 py-2.5 md:grid-cols-[1fr_1fr_auto] md:items-center" data-testid="site-text-row">
                <div className="min-w-0">
                  <div className="truncate text-sm text-white" title={x.t}>{x.t}</div>
                  <div className="truncate text-[10px] text-[#666]">{x.k === "attr" ? "Placeholder / tooltip" : x.k === "toast" ? "Notification" : "Text"} · {x.a.slice(0, 4).join(", ")}{x.a.length > 4 ? ` +${x.a.length - 4}` : ""}{live ? <span className="ml-1 text-[#22c55e]">· Live: “{live}”</span> : null}</div>
                </div>
                <input value={cur} placeholder="New text (leave empty to keep original)" onChange={(e) => setEdits((m) => ({ ...m, [x.t]: e.target.value }))}
                  data-testid="site-text-input" className={`w-full rounded-lg border bg-white/[0.03] px-2.5 py-1.5 text-sm text-white outline-none focus:border-[#00F0FF]/60 ${edits[x.t] !== undefined ? "border-[#FFC94D]/60" : draft[x.t] ? "border-[#00F0FF]/30" : "border-white/10"}`} />
                <button onClick={() => setEdits((m) => ({ ...m, [x.t]: "" }))} disabled={!cur} title="Back to original" data-testid="site-text-revert" className="justify-self-end rounded p-1.5 text-[#A1A1AA] hover:text-white disabled:opacity-30"><RotateCcw size={14} /></button>
              </div>
            );
          })}
          {rows.length > limit && <button onClick={() => setLimit((l) => l + PAGE * 2)} data-testid="site-text-more" className="w-full py-3 text-center text-xs font-semibold text-[#00F0FF] hover:bg-white/5">Show more ({rows.length - limit} left)</button>}
          {!rows.length && <p className="py-10 text-center text-sm text-[#666]">No text matches “{q}”.</p>}
        </div>
      )}
    </div>
  );
}
